import {
  Box,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  Button,
  useTheme,
  Snackbar,
  Alert,
  Skeleton,
  useMediaQuery,
  IconButton,
  darken,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { Add, Search, SwapVertOutlined } from "@mui/icons-material";

import { useOutletContext } from "react-router-dom";
import CardMapsHomeRp from "../../../../componentes/easyMaps/cardMapsHomeRP";
import Alerts from "../../../../componentes/easyMaps/alerts";
import NewMapFtth from "../../../../componentes/easyMaps/newMapFtth";
import MenuSort from "../../../../componentes/easyMaps/sideBarEasyMapsRp/menuSort";
import usePermissao from "../../../../hooks/usePermissao";

const useStyles = (theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    mt: 2,
    mb: 1,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    gap: 2,
  },
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 350px), 1fr))",
    gap: 2,
    width: "100%",
  },
  headerBox: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    width: "100%",
  },

  addIcon: { mt: -0.2 },
  button: {
    width: { xs: "100%", sm: "fit-content" },
    minWidth: "148px",
    textTransform: "none",
    boxShadow: "0px 0px 3px #0000001a",
    "&:hover": {
      backgroundColor: darken(theme.palette.primary.main, 0.2),
      boxShadow: "0px 0px 4px #0000001a",
    },
  },
  headerPaper: {
    boxShadow: "0px 0px 5px  #0000001a",
    borderTopLeftRadius: "0px",
    padding: "16px",
    display: "flex",
    gap: "16px",
    flexDirection: "column",
    position: "relative",
    width: "100%",
  },

  buttonDialog: {
    color: theme?.palette?.color?.buttonConfirm || "primary.main",
  },
  textfield: { width: "360px" },
  tabs: {
    zIndex: 1,
    ml: "-2px",
    "& button": {
      ml: "2px",
      mt: "1px",
      backgroundColor: theme.palette.background.tab,
      boxShadow: "0px 0px 5px  #0000001a",
      color: theme.palette.text.disable,
      textTransform: "none",
      borderTopLeftRadius: "5px",
      borderTopRightRadius: "5px",
      minWidth: "120px",
      minHeight: "50px",
    },
    "& button.Mui-selected": {
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiTabs-indicator": {
      backgroundColor: "transparent",
      border: "none",
    },
  },
});

export default function MapasFTTHEasyMaps() {
  const {
    maps,
    loadingPage,
    ftthLoadingPage,
    setMaps,
    setOpenConfig,
    config,
    api,
    removeMapFromState,
  } = useOutletContext();

  // Há fonte de dados cadastrada? Todas as fontes — QuestDB, bancos do ERP,
  // IXC (API), MyCore, SmartOLT — são entradas de `erpBancos`. Sem nenhuma, os
  // mapas permanecem, mas os cards ficam desabilitados e a mensagem de
  // configuração é exibida.
  const ftthConfig = config?.configuracoes?.ftth;
  const hasSource =
    Array.isArray(ftthConfig?.erpBancos) && ftthConfig.erpBancos.length > 0;

  // Status da sincronização (banco/Redis vazio + sync em andamento). Serve para
  // avisar o usuário que os dados ainda estão sendo carregados após trocar a
  // fonte, quando os cards aparecem temporariamente com 0.
  const [syncInfo, setSyncInfo] = useState(null);
  const prevEmptyRef = useRef(null);
  const prevSyncingRef = useRef(null);

  // Assinatura da fonte: muda quando o usuário troca grupo/virtualização ou
  // mexe nos cards, reiniciando o monitoramento do status de sincronização.
  // `integracao` (o grupo ativo) entra na assinatura porque trocá-lo muda o
  // conjunto de dados sem alterar a quantidade de cards em `erpBancos`.
  const sourceSig = `${ftthConfig?.integracao ?? ""}|${!!ftthConfig?.virtualizarDados}|${
    Array.isArray(ftthConfig?.erpBancos) ? ftthConfig.erpBancos.length : 0
  }`;

  useEffect(() => {
    if (!api || !hasSource) {
      setSyncInfo(null);
      return;
    }
    let cancelled = false;
    let timer = null;

    async function poll() {
      try {
        const { data } = await api.get("/easyMaps/onus/syncStatus");
        if (cancelled) return;
        setSyncInfo(data);

        // Recarrega os cards ao concluir: sair do estado vazio OU terminar a
        // sincronização. Na SmartOLT a base deixa de estar vazia no primeiro
        // ciclo, muito antes de a carga acabar — sem a segunda condição os
        // cards ficariam com a contagem parcial.
        const concluiu =
          (prevEmptyRef.current === true && data.empty === false) ||
          (prevSyncingRef.current === true && data.syncing === false);
        if (concluiu) {
          try {
            const res = await api.get("/easyMaps/map/findFtth");
            if (!cancelled && Array.isArray(res.data)) setMaps(res.data);
          } catch (e) {
            console.error("Falha ao recarregar mapas FTTH após sync:", e);
          }
        }
        prevEmptyRef.current = data.empty;
        prevSyncingRef.current = data.syncing;

        // Continua monitorando enquanto vazio ou sincronizando.
        if (!cancelled && (data.empty || data.syncing)) {
          timer = setTimeout(poll, 5000);
        }
      } catch (e) {
        if (!cancelled) timer = setTimeout(poll, 8000);
      }
    }

    prevEmptyRef.current = null;
    prevSyncingRef.current = null;
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, hasSource, sourceSig]);

  // `empty` sozinho não cobre a SmartOLT: a carga inicial é paginada e a base
  // deixa de estar vazia no primeiro ciclo, com 600 de 15 mil ONUs. Enquanto o
  // backend reporta `syncing`, o aviso continua.
  const showSyncBanner = !!(
    syncInfo &&
    hasSource &&
    (syncInfo.empty || syncInfo.syncing)
  );
  // Andamento da carga paginada (hoje só a SmartOLT reporta): "X de Y ONUs".
  // Fica vazio para as demais fontes, que carregam em um ciclo único.
  const andamentoSync = (() => {
    const p = syncInfo?.progresso;
    if (!p || p.fase !== "carga") return "";
    if (p.aguardandoLimite) {
      return " — pausado temporariamente pelo limite de chamadas da API";
    }
    if (p.carregadas == null || !p.totalItens) return "";
    const fmt = (n) => n.toLocaleString("pt-BR");
    return ` (${fmt(p.carregadas)} de ${fmt(p.totalItens)} ONUs)`;
  })();

  const [anchorElOrder, setAnchorElOrder] = useState(null);
  const [directionSort, setDirectionSort] = useState(false);
  const [sort, setSort] = useState(0);
  const [openNovoMapaDialog, setOpenNovoMapaDialog] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [textFilter, setTextFilter] = useState("");
  const handleCloseSnackbar = () => setSnackbar(null);
  const theme = useTheme();
  const { canEdit } = usePermissao("easyMaps");
  const styles = useStyles(theme);

  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const isAdmin =
    localStorage.getItem("1") &&
    (JSON.parse(localStorage.getItem("1")).usuarioCliente.codigoPermissao ===
      "adm001" ||
      JSON.parse(localStorage.getItem("1")).usuarioCliente.codigoPermissao ===
        "adm002");

  const mapasFilter = useMemo(() => {
    // Filtra
    let result = maps.filter(
      (map) =>
        map.titulo.toLowerCase().includes(textFilter.toLowerCase()) &&
        map.tipo === 3,
    );

    // Ordena baseado no critério
    const comparators = {
      0: (a, b) => a.titulo.localeCompare(b.titulo),
      1: (a, b) => {
        // Soma todos os valores (value) dos alertas
        const totalA =
          a.alertas?.reduce((acc, alerta) => acc + (alerta.value || 0), 0) || 0;
        const totalB =
          b.alertas?.reduce((acc, alerta) => acc + (alerta.value || 0), 0) || 0;
        return totalA - totalB;
      },
      2: (a, b) =>
        (a.mapas?.length + a.pops?.length + a.hosts?.length || 0) -
        (b.mapas?.length + b.pops?.length + b.hosts?.length || 0),
    };

    if (sort !== undefined && comparators[sort]) {
      result = result.sort((a, b) => {
        const comparison = comparators[sort](a, b);
        return directionSort === false ? comparison : -comparison;
      });
    }
    return result;
  }, [maps, textFilter, sort, directionSort]);

  const deleteMapsInMaps = removeMapFromState;

  // Recalcula os dados do card (onus/ctos/alertas) de um único mapa via backend
  // e mescla no estado, sem recarregar toda a lista. Usado após criar/editar
  // filtros, já que a criação/edição não retorna as estatísticas.
  async function refreshMapCard(mapId) {
    if (!mapId || !api) return;
    try {
      const { data } = await api.post("/easyMaps/map/ftth/findById", { mapId });
      if (data && data.id) {
        setMaps((prev) =>
          prev.map((m) => (m.id === data.id ? { ...m, ...data } : m)),
        );
      }
    } catch (error) {
      // Mantém os dados atuais do card em caso de falha.
      console.error("Falha ao atualizar dados do card FTTH:", error);
    }
  }

  function addMapInMaps(map) {
    const newMaps = [...maps];
    newMaps.push(map);
    setMaps(newMaps);
    refreshMapCard(map?.id);
  }

  function updateTitleInMaps(newTitle, mapId, ftthFilterConfig) {
    const newMaps = maps.map((map) => {
      if (map.id === mapId) {
        return {
          ...map,
          titulo: newTitle,
          ftthFilter:
            ftthFilterConfig !== undefined ? ftthFilterConfig : map.ftthFilter,
        };
      }
      return map;
    });
    setMaps(newMaps);
    refreshMapCard(mapId);
  }

  function updateMapTokenInMaps(mapToken, indexMap) {
    let newMaps = [...maps];
    newMaps[indexMap]["mapToken"] = mapToken;
    setMaps(newMaps);
  }

  function updateMapAccessInMaps(mapId, mapAccess) {
    setMaps((prev) =>
      prev.map((map) => (map.id === mapId ? { ...map, mapAccess } : map)),
    );
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.body}>
        <Stack width="100%">
          <Paper sx={styles.headerPaper}>
            <Box sx={styles.headerBox}>
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                flexWrap={"wrap"}
                gap={1}
              >
                <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                  <TextField
                    placeholder={"Buscar"}
                    autoComplete="off"
                    size="small"
                    fullWidth={isSmDown}
                    value={textFilter}
                    onChange={(event) => {
                      setTextFilter(event.target.value);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor:
                          theme.palette.background.paperLinkedMaps,
                        boxShadow: "none",
                        width: { xs: "100%", sm: "300px" },
                        "&:hover": {
                          backgroundColor:
                            theme.palette.background.paperLinkedMaps,
                        },
                      },
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Search />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <IconButton
                    size="small"
                    sx={{ width: "40px", height: "40px" }}
                    onClick={(event) => setAnchorElOrder(event.currentTarget)}
                  >
                    <SwapVertOutlined />
                  </IconButton>
                </Stack>

                {canEdit && (
                  <Button
                    color="primary"
                    variant="contained"
                    sx={styles.button}
                    endIcon={<Add />}
                    disabled={!hasSource}
                    onClick={() => {
                      setOpenNovoMapaDialog(true);
                    }}
                  >
                    Novo Mapa
                  </Button>
                )}
              </Stack>
            </Box>
          </Paper>
        </Stack>

        {loadingPage ? (
          <Box sx={styles.cardsGrid}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                sx={{
                  width: "100%",
                  height: "180px",
                  borderRadius: "6px",
                }}
              />
            ))}
          </Box>
        ) : (
          <>
            {!hasSource &&
              (ftthLoadingPage || !config ? (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    paddingLeft: 2,
                    alignItems: "start",
                    width: "100%",
                    flexWrap: "wrap",
                  }}
                >
                  {Array.from({ length: 1 }).map((_, index) => (
                    <Skeleton
                      key={index}
                      variant="rectangular"
                      sx={{
                        width: "360px",
                        height: "180px",
                        borderRadius: "6px",
                        flex: "0 1 360px",
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Stack width={"100%"}>
                  <Alerts
                    sx={{ alignItems: "start" }}
                    severity="info"
                    message={
                      <Stack
                        direction={"column"}
                        alignItems={"start"}
                        sx={{ width: "100%" }}
                        gap="10px"
                      >
                        <Box>
                          O mapa de FTTH (Fiber to the Home) é gerado
                          automaticamente pelo sistema. Se não estiver
                          disponível, verifique o link fornecido nas
                          configurações do banco. Caso o problema persista,
                          entre em contato com o suporte.
                        </Box>

                        {isAdmin && (
                          <Button
                            onClick={() =>
                              setOpenConfig({ open: true, tab: 0 })
                            }
                            sx={{ fontSize: "12px", p: "5px" }}
                            variant="outlined"
                            color="inherit"
                          >
                            Acessar configurações
                          </Button>
                        )}
                      </Stack>
                    }
                  ></Alerts>
                </Stack>
              ))}
            {showSyncBanner && (
              <Stack width={"100%"}>
                <Alerts
                  sx={{ alignItems: "start" }}
                  severity={syncInfo?.syncing ? "info" : "warning"}
                  message={
                    <Box>
                      {syncInfo?.syncing
                        ? `Sincronização em andamento — os dados do FTTH (${
                            syncInfo?.destino === "redis" ? "Redis" : "banco"
                          }) estão sendo carregados${andamentoSync}. Os mapas serão atualizados automaticamente ao concluir.`
                        : `Os dados do FTTH ainda não estão disponíveis (${
                            syncInfo?.destino === "redis" ? "Redis" : "banco"
                          } vazio). Aguardando a próxima sincronização — os mapas podem exibir 0 até lá.`}
                    </Box>
                  }
                ></Alerts>
              </Stack>
            )}
            <Box sx={styles.cardsGrid}>
              {Array.isArray(mapasFilter) &&
                mapasFilter.length > 0 &&
                mapasFilter.map((map, index) => (
                  <CardMapsHomeRp
                    key={map.id}
                    index={index}
                    id={map.id}
                    mapName={map.titulo}
                    tipo={map.tipo}
                    inGroup={false}
                    disabled={!hasSource}
                    alertas={map.alertas}
                    hostsCounts={map.onus || [0]}
                    mapasCounts={map.ctos || [0]}
                    updateMapTokenInMaps={updateMapTokenInMaps}
                    setSnackbar={setSnackbar}
                    mapas={map?.mapas}
                    pops={map?.pops}
                    mapToken={map?.mapToken}
                    mapAccess={map?.mapAccess}
                    deleteMapsInMaps={deleteMapsInMaps}
                    updateTitleInMaps={updateTitleInMaps}
                    updateMapAccessInMaps={updateMapAccessInMaps}
                    ftthFilter={map.ftthFilter}
                  />
                ))}
            </Box>
          </>
        )}
      </Box>
      <NewMapFtth
        key={"newMapaFtth-Main"}
        openNovoMapaDialog={openNovoMapaDialog}
        setOpenNovoMapaDialog={setOpenNovoMapaDialog}
        setSnackbar={setSnackbar}
        addMapInMaps={addMapInMaps}
      />
      <MenuSort
        anchorEl={anchorElOrder}
        setAnchorEl={setAnchorElOrder}
        options={["Nome", "Alertas", "Elementos"]}
        direction={directionSort}
        setDirection={setDirectionSort}
        sort={sort}
        setSort={setSort}
      />

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={2000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={handleCloseSnackbar} />
        </Snackbar>
      )}
    </Box>
  );
}
